import { gql } from '@apollo/client';

const taskFragment = gql`
  fragment TaskParts on Task {
    id
    type
    status
    resolution
    createdOn
    assigneeType
    assignedPerson {
      id
      fullName
    }
    job {
      id
      type
      priority
      dueDate
      client {
        id
        businessName
        inboxState
      }
      taskNotes {
        id
        body
        createdBy {
          id
          fullName
        }
        createdOn
      }
      triggerContext {
        ... on CategorizeClientTransactionsTriggerContext {
          oldestTransactionDate
          numNeedsReview
          numAwaitingCategory
          totalAmountAwaitingCategory
        }
        ... on TriageClientDocumentsTriggerContext {
          numUnknownDocs
        }
        ... on BulkAdjustmentTriggerContext {
          numMonthsAdjustmentRequired
          startDate
          endDate
        }
        ... on CollectAllLedgerStatementsTriggerContext {
          ledgerDateActive
        }
        ... on ReviewGeneralLedgerTriggerContext {
          startDate
          endDate
        }
        ... on ResolveIncorrectAdjustmentTriggerContext {
          numMonths
          startDate
          endDate
        }
        ... on ResolveIncorrectBalanceTriggerContext {
          numMonths
          startDate
          endDate
        }
        ... on ResolveIncorrectBooksTriggerContext {
          numMonths
          startDate
          endDate
        }
        ... on ResolveIncorrectCategorizationTriggerContext {
          numMonths
          startDate
          endDate
        }
        ... on SetupNewConnectionTriggerContext {
          institution
          source
          numAccounts
        }
      }
      parameters {
        ... on ResolveBalanceCheckerJobParameters {
          ledgerAccount {
            name
          }
        }
        ... on ResolvePlaidConnectionJobParameters {
          ledgerAccount {
            name
          }
        }
        ... on ResolveDocumentAdjustmentJobParameters {
          addOn {
            id
            name
          }
          document {
            vendorName
            name
            date
          }
        }
        ... on ResolveThirdPartyLoginAdjustmentJobParameters {
          addOn {
            id
            name
          }
          yearMonth
        }
        ... on ResolveBulkAdjustmentJobParameters {
          addOn {
            id
            name
          }
        }
        ... on CollectMonthlyLedgerStatementJobParameters {
          ledgerAccount {
            id
            name
          }
          statementMonth
        }
        ... on CollectAllLedgerStatementsJobParameters {
          ledgerAccount {
            id
            name
          }
        }
        ... on ResolveIncorrectAdjustmentJobParameters {
          reviewPeriod
        }
        ... on ResolveIncorrectBalanceJobParameters {
          reviewPeriod
        }
        ... on ResolveIncorrectBooksJobParameters {
          reviewPeriod
        }
        ... on ResolveIncorrectCategorizationJobParameters {
          reviewPeriod
        }
        ... on SetupNewConnectionJobParameters {
          connectionId
        }
      }
      escalatedOn
      escalatedBy {
        id
      }
    }
  }
`;

const GET_LOGGED_IN_PERSON = gql`
  query getLoggedInPerson {
    loggedInPerson {
      id
      fullName
    }
  }
`;

const ASSIGN_TASK = gql`
  mutation assignTask($assignedPersonId: Int, $taskId: ID!) {
    assignTask(taskId: $taskId, assignedPersonId: $assignedPersonId) {
      ...TaskParts
    }
  }
  ${taskFragment}
`;

const RESOLVE_TASK = gql`
  mutation resolveTask($taskId: ID!, $resolution: GraphQLTaskResolution!, $note: String) {
    resolveTask(taskId: $taskId, input: { resolution: $resolution, note: $note }) {
      task {
        ...TaskParts
      }
    }
  }
  ${taskFragment}
`;

const GET_TASKS = gql`
  query getTasks($filterBy: TasksFilterInput, $pagination: PaginationInput) {
    tasks(filterBy: $filterBy, pagination: $pagination) {
      tasks {
        ...TaskParts
      }
      pageInfo {
        startCursor
        endCursor
        total
        hasNextPage
        hasPrevPage
      }
    }
  }
  ${taskFragment}
`;

const GET_CLIENT = gql`
  query getClient($clientId: ID!) {
    client(id: $clientId) {
      id
      delegatedBookkeeper {
        id
      }
    }
  }
`;

const GET_ASSIGNED_SPECIALISTS = gql`
  query {
    assignedSpecialists {
      fullName
      id
    }
  }
`;

const GET_RESOLUTIONS = gql`
  query getTaskResolutions($taskType: TaskType) {
    taskResolutions(taskType: $taskType)
  }
`;

const GET_JOB_TYPES = gql`
  query {
    jobTypes
  }
`;

const GET_TASK_TYPES = gql`
  query getTaskTypes($jobTypes: [JobType!]) {
    taskTypes(jobTypes: $jobTypes)
  }
`;

const GET_MANUAL_TASKS_RESOLUTIONS = gql`
  query {
    manualTasksResolutions {
      jobType
      taskType
      assigneeType
      manualResolutions
    }
  }
`;

const GET_JOB_HISTORY = gql`
  query getJobHistory($jobId: ID!) {
    job(id: $jobId) {
      id
      tasks {
        id
        type
        status
        resolution
        createdOn
        assignedOn
        resolvedOn
        assigneeType
        assignedPerson {
          id
          fullName
        }
      }
    }
  }
`;

export {
  GET_LOGGED_IN_PERSON,
  ASSIGN_TASK,
  RESOLVE_TASK,
  GET_TASKS,
  GET_CLIENT,
  GET_ASSIGNED_SPECIALISTS,
  GET_RESOLUTIONS,
  GET_JOB_TYPES,
  GET_TASK_TYPES,
  GET_MANUAL_TASKS_RESOLUTIONS,
  GET_JOB_HISTORY,
};
